/**
 * SMF (Standard MIDI File) format 1 の最小ライター。
 * 依存なしでレイヤーごとの PhraseNote 配列を .mid バイナリに変換する。
 * トラック 0 = テンポ/拍子メタ、以降はレイヤーごとのノートトラック。
 */

import { PPQ, type PhraseNote } from "./phraseGenerator";

/** MIDI の可変長数量（Variable-Length Quantity）エンコード */
export const encodeVlq = (value: number): number[] => {
  if (value < 0) throw new Error(`VLQ は負値を扱えません: ${value}`);
  const bytes: number[] = [value & 0x7f];
  let v = value >> 7;
  while (v > 0) {
    bytes.unshift((v & 0x7f) | 0x80);
    v >>= 7;
  }
  return bytes;
};

/** 1 トラック分の書き出しデータ */
export interface MidiTrackData {
  /** トラック名メタ（DAW での識別用、ASCII 推奨） */
  name: string;
  /** MIDI チャンネル 0..15（0 = ch1、9 = ch10） */
  channel: number;
  notes: PhraseNote[];
}

interface MidiEvent {
  tick: number;
  /** 同 tick 内の並び順（note off を note on より先に置く） */
  order: number;
  bytes: number[];
}

const str = (s: string): number[] => Array.from(s, (c) => c.charCodeAt(0));

const u32 = (n: number): number[] => [
  (n >>> 24) & 0xff,
  (n >>> 16) & 0xff,
  (n >>> 8) & 0xff,
  n & 0xff,
];

const u16 = (n: number): number[] => [(n >>> 8) & 0xff, n & 0xff];

/** イベント列 → MTrk チャンク（デルタタイム化 + End of Track 付与） */
const buildTrackChunk = (events: MidiEvent[]): number[] => {
  events.sort((a, b) => a.tick - b.tick || a.order - b.order);
  const body: number[] = [];
  let prevTick = 0;
  for (const ev of events) {
    body.push(...encodeVlq(ev.tick - prevTick), ...ev.bytes);
    prevTick = ev.tick;
  }
  body.push(...encodeVlq(0), 0xff, 0x2f, 0x00);
  return [...str("MTrk"), ...u32(body.length), ...body];
};

/**
 * レイヤー別トラック → SMF format 1 バイト列。
 * 負の tick は保険として 0 にクランプする（MIDI は負の時刻を表現できない。
 * 現行のアレンジメントはメロディ側のシフトにより常に非負を保証している）。
 */
export const encodeSmf = (
  tracks: MidiTrackData[],
  bpm: number,
): Uint8Array<ArrayBuffer> => {
  // トラック 0: テンポ（μs / 四分音符）と拍子（4/4）
  const usPerQuarter = Math.round(60_000_000 / bpm);
  const metaEvents: MidiEvent[] = [
    {
      tick: 0,
      order: 0,
      bytes: [
        0xff, 0x51, 0x03,
        (usPerQuarter >>> 16) & 0xff,
        (usPerQuarter >>> 8) & 0xff,
        usPerQuarter & 0xff,
      ],
    },
    { tick: 0, order: 0, bytes: [0xff, 0x58, 0x04, 4, 2, 24, 8] },
  ];

  const chunks: number[][] = [buildTrackChunk(metaEvents)];

  for (const track of tracks) {
    const channel = track.channel & 0x0f;
    const events: MidiEvent[] = [
      {
        tick: 0,
        order: 0,
        bytes: [0xff, 0x03, ...encodeVlq(track.name.length), ...str(track.name)],
      },
    ];
    for (const note of track.notes) {
      const onTick = Math.max(0, note.tick);
      events.push({
        tick: onTick,
        order: 2,
        bytes: [0x90 | channel, note.midiNote & 0x7f, note.velocity & 0x7f],
      });
      events.push({
        tick: onTick + note.durationTicks,
        order: 1,
        bytes: [0x80 | channel, note.midiNote & 0x7f, 0x40],
      });
    }
    chunks.push(buildTrackChunk(events));
  }

  const header = [
    ...str("MThd"),
    ...u32(6),
    ...u16(1), // format 1
    ...u16(chunks.length),
    ...u16(PPQ),
  ];

  return new Uint8Array([...header, ...chunks.flat()]);
};

/** バイナリを Blob 化してブラウザにダウンロードさせる */
export const downloadBlob = (
  data: Uint8Array<ArrayBuffer> | ArrayBuffer,
  filename: string,
  mimeType: string,
): void => {
  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadMidi = (
  tracks: MidiTrackData[],
  bpm: number,
  filename: string,
): void => {
  downloadBlob(encodeSmf(tracks, bpm), filename, "audio/midi");
};
